import React, { useEffect, useState, useRef, lazy } from 'react';
function useScrollReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(40px)';
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => {
          el.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        }, delay);
        observer.disconnect();
      }
    }, {
      threshold: 0.1
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
}
export function LocationCTA() {
  const headerRef = useScrollReveal(0);
  const infoRef = useScrollReveal(200);
  const ctaRef = useScrollReveal(350);
  const mapRef = useScrollReveal(500);
  return <section id="location" className="w-full py-20 sm:py-28 relative overflow-hidden" style={{
    backgroundColor: '#0a0a0a'
  }} aria-label="Location and contact" data-id="element-229">
      {/* Gold glow */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at 50% 100%, rgba(201,168,76,0.06) 0%, transparent 60%)'
    }} aria-hidden="true" data-id="element-230" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-231">
        <div ref={headerRef} className="text-center mb-14" data-id="element-232">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-233">
            <div style={{
            width: '40px',
            height: '1px',
            background: '#C9A84C'
          }} data-id="element-234" />
            <span className="section-label" data-id="element-235">Find Us</span>
            <div style={{
            width: '40px',
            height: '1px',
            background: '#C9A84C'
          }} data-id="element-236" />
          </div>
          <h2 style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
          fontWeight: 700,
          color: '#F5F5F5',
          lineHeight: 1.2,
          marginBottom: '1rem'
        }} data-id="element-237">
            We're Right Here —{' '}
            <span style={{
            color: '#C9A84C',
            fontStyle: 'italic'
          }} data-id="element-238">
              Come See Us
            </span>
          </h2>
          <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '1.05rem',
          color: '#888',
          lineHeight: 1.7
        }} data-id="element-239">
            This part is simple. The next time you need a cut — walk in.
          </p>
        </div>

        {/* Info cards */}
        <div ref={infoRef} className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-12" data-id="element-240">
          <InfoCard icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-242">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" data-id="element-243" />
                <circle cx="12" cy="10" r="3" data-id="element-244" />
              </svg>} label="Address" value="Paraíso Plaza 2" sub="3300 W 84th St, Hialeah, FL 33018" href="https://maps.google.com/?q=3300+W+84th+St+Hialeah+FL+33018" data-id="element-241" />
          <InfoCard icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-246">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.58 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.56a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" data-id="element-247" />
              </svg>} label="Phone" value="(786) 536-4035" sub="Call or walk in anytime" href="tel:7865364035" data-id="element-245" />
          <InfoCard icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-249">
                <circle cx="12" cy="12" r="10" data-id="element-250" />
                <polyline points="12 6 12 12 16 14" data-id="element-251" />
              </svg>} label="Hours" value="Open Until 8PM" sub="Walk-ins always welcome" data-id="element-248" />
        </div>

        {/* Main CTA block */}
        <div ref={ctaRef} data-id="element-252">
          <div className="p-8 sm:p-12 rounded-sm text-center" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(201,168,76,0.3)',
          background: 'linear-gradient(135deg, #111111 0%, #1a1a1a 100%)'
        }} data-id="element-253">
            <h3 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: 'clamp(1.5rem, 3.5vw, 2.2rem)',
            fontWeight: 900,
            color: '#F5F5F5',
            lineHeight: 1.3,
            marginBottom: '1rem'
          }} data-id="element-254">
              Your best cut is one walk-in away.
            </h3>
            <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '1.05rem',
            color: '#888',
            lineHeight: 1.7,
            maxWidth: '500px',
            margin: '0 auto 2.5rem'
          }} data-id="element-255">
              No appointment. No waiting weeks. No explaining yourself to
              someone who won't remember you next time. Just walk in, sit down,
              and leave looking exactly the way you should.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-256">
              <a href="https://maps.google.com/?q=3300+W+84th+St+Hialeah+FL+33018" target="_blank" rel="noopener noreferrer" className="btn-gold inline-flex items-center justify-center gap-3 px-8 py-4 text-base rounded-sm" data-id="element-257">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-258">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" data-id="element-259" />
                  <circle cx="12" cy="10" r="3" data-id="element-260" />
                </svg>
                Get Directions
              </a>
              <a href="tel:7865364035" className="inline-flex items-center justify-center gap-3 px-8 py-4 text-base rounded-sm transition-all hover:opacity-80" style={{
              border: '1px solid rgba(201,168,76,0.5)',
              color: '#C9A84C',
              fontFamily: 'var(--font-callout)',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase'
            }} data-id="element-261">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-262">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.58 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.56a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" data-id="element-263" />
                </svg>
                Call Now
              </a>
            </div>

            <p className="mt-6" style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.85rem',
            color: '#555',
            letterSpacing: '0.05em',
            textTransform: 'uppercase'
          }} data-id="element-264">
              Walk-ins welcome · Kids welcome · Excellence guaranteed
            </p>
          </div>
        </div>

        {/* Holographic Map */}
        <div ref={mapRef} className="mt-10" data-id="element-265">
          <HoloMap data-id="element-266" />
        </div>
      </div>
    </section>;
}
function HoloMap() {
  const [loaded, setLoaded] = useState(false);
  return <div className="relative rounded-sm overflow-hidden" style={{
    border: '1px solid rgba(201,168,76,0.4)',
    boxShadow: '0 0 40px rgba(201,168,76,0.08), 0 0 80px rgba(201,168,76,0.04)'
  }} data-id="element-267">
      {/* HUD corner brackets */}
      <div className="absolute top-0 left-0 z-20 pointer-events-none" aria-hidden="true" data-id="element-268">
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" data-id="element-269">
          <path d="M2 16 L2 2 L16 2" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" data-id="element-270" />
        </svg>
      </div>
      <div className="absolute top-0 right-0 z-20 pointer-events-none" aria-hidden="true" data-id="element-271">
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" data-id="element-272">
          <path d="M30 16 L30 2 L16 2" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" data-id="element-273" />
        </svg>
      </div>
      <div className="absolute bottom-0 left-0 z-20 pointer-events-none" aria-hidden="true" data-id="element-274">
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" data-id="element-275">
          <path d="M2 16 L2 30 L16 30" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" data-id="element-276" />
        </svg>
      </div>
      <div className="absolute bottom-0 right-0 z-20 pointer-events-none" aria-hidden="true" data-id="element-277">
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" data-id="element-278">
          <path d="M30 16 L30 30 L16 30" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" data-id="element-279" />
        </svg>
      </div>

      {/* Scan line animation */}
      <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{
      height: '2px',
      background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.6), transparent)',
      animation: 'holo-scan 3s linear infinite',
      top: 0
    }} aria-hidden="true" data-id="element-280" />

      {/* Grid overlay */}
      <div className="absolute inset-0 z-10 pointer-events-none" style={{
      backgroundImage: `
            linear-gradient(rgba(201,168,76,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(201,168,76,0.04) 1px, transparent 1px)
          `,
      backgroundSize: '40px 40px'
    }} aria-hidden="true" data-id="element-281" />

      {/* Top HUD bar */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 py-2 pointer-events-none" style={{
      background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)'
    }} aria-hidden="true" data-id="element-282">
        <div className="flex items-center gap-2" data-id="element-283">
          <div style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          backgroundColor: '#C9A84C',
          animation: 'holo-pulse 2s ease-in-out infinite'
        }} data-id="element-284" />
          <span style={{
          fontFamily: 'var(--font-callout)',
          fontSize: '0.65rem',
          color: '#C9A84C',
          letterSpacing: '0.15em',
          textTransform: 'uppercase'
        }} data-id="element-285">
            Paraíso Plaza 2 · Hialeah, FL
          </span>
        </div>
        <span style={{
        fontFamily: 'var(--font-callout)',
        fontSize: '0.6rem',
        color: 'rgba(201,168,76,0.5)',
        letterSpacing: '0.1em'
      }} data-id="element-286">
          3300 W 84TH ST
        </span>
      </div>

      {/* Bottom HUD bar */}
      <div className="absolute bottom-0 left-0 right-0 z-20 flex items-center justify-between px-4 py-2 pointer-events-none" style={{
      background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)'
    }} aria-hidden="true" data-id="element-287">
        <span style={{
        fontFamily: 'var(--font-callout)',
        fontSize: '0.6rem',
        color: 'rgba(201,168,76,0.5)',
        letterSpacing: '0.1em'
      }} data-id="element-288">
          HIALEAH · FL 33018
        </span>
        <div className="flex items-center gap-2" data-id="element-289">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-290">
            <circle cx="12" cy="12" r="10" data-id="element-291" />
            <polyline points="12 6 12 12 16 14" data-id="element-292" />
          </svg>
          <span style={{
          fontFamily: 'var(--font-callout)',
          fontSize: '0.6rem',
          color: '#C9A84C',
          letterSpacing: '0.1em'
        }} data-id="element-293">
            OPEN UNTIL 8PM
          </span>
        </div>
      </div>

      {/* Loading state */}
      {!loaded && <div className="absolute inset-0 z-30 flex items-center justify-center" style={{
      backgroundColor: '#0a0a0a'
    }} data-id="element-294">
          <div className="flex flex-col items-center gap-3" data-id="element-295">
            <div style={{
          width: '32px',
          height: '32px',
          border: '2px solid rgba(201,168,76,0.2)',
          borderTop: '2px solid #C9A84C',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} data-id="element-296" />
            <span style={{
          fontFamily: 'var(--font-callout)',
          fontSize: '0.7rem',
          color: '#C9A84C',
          letterSpacing: '0.15em'
        }} data-id="element-297">
              LOADING MAP...
            </span>
          </div>
        </div>}

      {/* Google Maps iframe */}
      <iframe title="Paraíso Barbershop Location" src="https://maps.google.com/maps?q=3300+W+84th+St,+Hialeah,+FL+33018&output=embed&z=16" width="100%" height="380" style={{
      border: 'none',
      display: 'block',
      filter: 'invert(90%) hue-rotate(180deg) saturate(0.8) brightness(0.85)',
      opacity: loaded ? 1 : 0,
      transition: 'opacity 0.5s ease'
    }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" onLoad={() => setLoaded(true)} aria-label="Map showing By Guerra Barbershop at 3300 W 84th St, Hialeah, FL 33018" data-id="element-298" />
    </div>;
}
function InfoCard({
  icon,
  label,
  value,
  sub,
  href
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  href?: string;
}) {
  const content = <div className="p-6 rounded-sm flex flex-col gap-3 h-full" style={{
    backgroundColor: '#111111',
    border: '1px solid rgba(201,168,76,0.15)'
  }} data-id="element-299">
      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
      backgroundColor: 'rgba(201,168,76,0.1)',
      border: '1px solid rgba(201,168,76,0.25)'
    }} data-id="element-300">
        {icon}
      </div>
      <div data-id="element-301">
        <p style={{
        fontFamily: 'var(--font-callout)',
        fontSize: '0.7rem',
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        color: '#C9A84C',
        marginBottom: '0.25rem'
      }} data-id="element-302">
          {label}
        </p>
        <p style={{
        fontFamily: 'var(--font-callout)',
        fontSize: '1.05rem',
        fontWeight: 600,
        color: '#F5F5F5',
        letterSpacing: '0.02em'
      }} data-id="element-303">
          {value}
        </p>
        <p style={{
        fontFamily: 'var(--font-body)',
        fontSize: '0.85rem',
        color: '#888',
        marginTop: '0.25rem'
      }} data-id="element-304">
          {sub}
        </p>
      </div>
    </div>;
  if (href) {
    return <a href={href} className="block transition-opacity hover:opacity-80" target={href.startsWith('http') ? '_blank' : undefined} rel={href.startsWith('http') ? 'noopener noreferrer' : undefined} data-id="element-305">
        {content}
      </a>;
  }
  return content;
}